import { Grid, GridItem, SimpleGrid } from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import { djs } from '@duke-hq/djs';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { StudentEnrolmentHealth } from '@duke-hq/svc-api-db';

import useDashboardContext from '../../context/useDashboardContext';
import buildLinkToEnrolments from '../../utils/buildLinkToEnrolments';
import ErrorFallback from '../ErrorFallback';
import CardGroup from './shared/CardGroup';
import Section from './shared/Section';
import StatCard from './shared/StatCard';

function getNextFourWeeksRange() {
  const today = djs();
  const fourWeeksLater = today.add(4, 'week');

  if (today.month() === fourWeeksLater.month()) {
    return `${today.format('D')} - ${fourWeeksLater.format('D MMM YYYY')}`;
  } else {
    return `${today.format('D MMM')} - ${fourWeeksLater.format('D MMM YYYY')}`;
  }
}

type Props = {
  data?: RouterOutputs['dashboard']['getDueForSemester'];
};

function DueForCompletionUI({ data }: Props) {
  const { openPanel } = useDashboardContext();
  const { recentlyCompleted, progressReports, incomplete, behind, slipping, onTrack, notStarted } =
    data ?? {};

  return (
    <Grid w="full" templateColumns="repeat(6, 1fr)" gap="4">
      <GridItem colSpan={{ base: 6, md: 4, xl: 2 }}>
        <CardGroup
          title={t`Completions and progression`}
          openPanel={() => openPanel('CompletionsProgressions', { category: 'all' })}
        >
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="8px">
            <StatCard
              backgroundColour="success"
              title={t`Recently completed`}
              titleColour="fg.inverse-subtle"
              description={t`Last 14 days`}
              descriptionColour="whiteAlpha.900"
              value={recentlyCompleted}
              valueColour="fg.inverse"
              onClick={() => openPanel('CompletionsProgressions', { category: 'completions' })}
            />
            <StatCard
              backgroundColour="info"
              title={t`Progress reports and VOE`}
              titleColour="fg.inverse-subtle"
              description={t`Last 14 days`}
              descriptionColour="whiteAlpha.900"
              value={progressReports}
              valueColour="fg.inverse"
              onClick={() => openPanel('CompletionsProgressions', { category: 'progressions' })}
            />
          </SimpleGrid>
        </CardGroup>
      </GridItem>
      <GridItem colSpan={{ base: 6, md: 2, xl: 1 }}>
        <CardGroup title={t`Past due`} openPanel={() => openPanel('PastDue')}>
          <StatCard
            backgroundColour="danger.emphasis"
            title={t`Incomplete`}
            titleColour="fg.inverse-subtle"
            description={t`Past due`}
            descriptionColour="whiteAlpha.900"
            value={incomplete}
            valueColour="fg.inverse"
            onClick={() => openPanel('PastDue')}
          />
        </CardGroup>
      </GridItem>
      <GridItem colSpan={{ base: 6, md: 6, xl: 3 }}>
        <CardGroup
          title={t`Due next 4 weeks`}
          subTitle={getNextFourWeeksRange()}
          openPanel={() => openPanel('DueNextMonth')}
        >
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing="8px">
            <StatCard
              backgroundColour="bg.canvas"
              title={t`Not started`}
              titleColour="fg.muted"
              description=""
              descriptionColour="danger.emphasis"
              value={notStarted}
              valueColour="fg.emphasis"
              to={buildLinkToEnrolments({
                dueDateStart: djs.utc(),
                dueDateEnd: djs.utc().add(28, 'days'),
                health: StudentEnrolmentHealth.NOT_STARTED,
              })}
            />

            <StatCard
              backgroundColour="bg.canvas"
              title={t`Behind`}
              titleColour="fg.muted"
              description={t`>2 units behind pace`}
              descriptionColour="danger.emphasis"
              value={behind}
              valueColour="fg.emphasis"
              onClick={() =>
                openPanel('TwoWeekEngagement', {
                  health: StudentEnrolmentHealth.BEHIND,
                })
              }
            />
            <StatCard
              backgroundColour="bg.canvas"
              title={t`Slipping`}
              titleColour="fg.muted"
              description={t`<2 units behind pace`}
              descriptionColour="yellow.500"
              value={slipping}
              valueColour="fg.emphasis"
              onClick={() =>
                openPanel('TwoWeekEngagement', {
                  health: StudentEnrolmentHealth.SLIPPING,
                })
              }
            />
            <StatCard
              backgroundColour="bg.canvas"
              title={t`On track`}
              titleColour="fg.muted"
              description={t`<1 unit behind pace`}
              descriptionColour="success"
              value={onTrack}
              valueColour="fg.emphasis"
              onClick={() =>
                openPanel('TwoWeekEngagement', {
                  health: StudentEnrolmentHealth.ON_TRACK,
                })
              }
            />
          </SimpleGrid>
        </CardGroup>
      </GridItem>
    </Grid>
  );
}

function DueForCompletionWithData() {
  const { filters } = useFiltersContext();
  const userUTCOffset = djs().utcOffset();
  const { data } = trpcTenant.dashboard.getDueForSemester.useQuery(
    { filters, userUTCOffset },
    {
      suspense: true,
      trpc: {
        context: {
          skipBatch: true,
        },
      },
    },
  );

  if (!data || Object.keys(data).length === 0) {
    return <ErrorFallback />;
  }

  return <DueForCompletionUI data={data} />;
}

export default function DueForCompletion() {
  return (
    <Section
      title={t`Due for semester completion`}
      tooltipText={t`Enrolments with current Semester due for completion`}
    >
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Suspense fallback={<DueForCompletionUI />}>
          <DueForCompletionWithData />
        </Suspense>
      </ErrorBoundary>
    </Section>
  );
}
